import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageHeader, SectionCard } from '../components/layout/memberPrimitives';
import Spinner from '../components/ui/Spinner';
import FeedbackDisclaimerCard from '../components/feedback/FeedbackDisclaimerCard';
import FeedbackThread from '../components/feedback/FeedbackThread';
import FeedbackComposer from '../components/feedback/FeedbackComposer';
import FeedbackSuggestedPrompts from '../components/feedback/FeedbackSuggestedPrompts';
import FeedbackEscalateButton from '../components/feedback/FeedbackEscalateButton';
import FeedbackConversationHistory from '../components/feedback/FeedbackConversationHistory';
import { useFeedbackChat } from '../hooks/useFeedbackChat';

/**
 * Ask Melio — the test-cohort feedback surface.
 *
 * A dedicated route rather than a floating launcher or a drawer. MemberShell
 * hardcodes an 80px left rail with no breakpoint, there is no overlay or toast
 * primitive in the member app, and EventModal is max-w-lg — none of which suits
 * a multi-turn thread. A page inherits whatever responsive behaviour the rest
 * of the portal has, needs no new layout primitives, and is deep-linkable.
 *
 * The flag here only hides UI. The real gate is server-side and additionally
 * refuses to run outside a non-production project.
 */

export default function FeedbackChatPage() {
  const location = useLocation();
  const openedFrom = new URLSearchParams(location.search).get('from') || location.pathname;

  const chat = useFeedbackChat(openedFrom);

  if (chat.loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  // Not in this environment — behave as though the route does not exist.
  if (!chat.available) {
    return <Navigate to="/home" replace />;
  }

  const notMonitored = chat.bootstrap?.disclaimer?.notMonitored;
  const showSuggestions = chat.messages.length === 0;
  const aiOff = chat.bootstrap?.aiRepliesEnabled === false;

  return (
    <div className="pb-8">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Ask Melio' }]}
        eyebrow="Test cohort"
        title="Questions and feedback"
        subtitle="Tell us what was confusing, what broke, or what you expected to happen. You can also ask about your own results."
      />

      {!chat.acknowledged && chat.bootstrap && (
        <FeedbackDisclaimerCard bootstrap={chat.bootstrap} onAcknowledge={chat.acknowledge} />
      )}

      {chat.acknowledged && (
        <SectionCard
          title="Ask Melio"
          rightSlot={(
            <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: 'var(--melio-text-muted)' }}>
              AI assistant
            </span>
          )}
        >
          {/* Persistent, non-collapsible. A member should never have to scroll
              or expand anything to find out this channel is not monitored. */}
          {notMonitored && (
            <div
              className="rounded-2xl px-4 py-3 mb-4 text-sm leading-relaxed"
              style={{
                backgroundColor: 'var(--melio-cream)',
                border: '1px solid var(--melio-border)',
                color: 'var(--melio-terracotta-deep)',
              }}
            >
              {notMonitored}
            </div>
          )}

          {aiOff && (
            <div
              className="rounded-2xl px-4 py-3 mb-4 text-sm"
              style={{ backgroundColor: 'var(--melio-cream)', color: 'var(--melio-text-muted)' }}
            >
              Replies are switched off at the moment, but everything you write is still
              saved for the Melio team.
            </div>
          )}

          {showSuggestions && chat.bootstrap?.suggestedPrompts && (
            <FeedbackSuggestedPrompts
              prompts={chat.bootstrap.suggestedPrompts}
              onSelect={(prompt) => chat.send(prompt)}
              disabled={chat.sendState === 'sending'}
            />
          )}

          {chat.messages.length > 0 && (
            <FeedbackThread
              messages={chat.messages}
              sending={chat.sendState === 'sending'}
              onRate={chat.rate}
            />
          )}

          <FeedbackComposer
            value={chat.draft}
            onChange={chat.setDraft}
            onSend={() => chat.send()}
            onRetry={chat.retry}
            state={chat.sendState}
            errorMessage={chat.errorMessage}
            maxChars={chat.maxChars}
            disabled={chat.atMessageLimit}
            disabledReason={chat.atMessageLimit
              ? 'This conversation has got quite long. Everything here has been saved — please start a new one if you have more to add.'
              : null}
          />

          {chat.conversationId && (
            <div className="pt-2">
              <FeedbackEscalateButton onEscalate={chat.escalate} />
            </div>
          )}

          <FeedbackConversationHistory
            conversations={chat.conversations}
            activeId={chat.conversationId}
            onOpen={chat.openConversation}
            onStartNew={chat.startNewConversation}
          />
        </SectionCard>
      )}
    </div>
  );
}
